import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  selectIsAllowedToPublish,
  selectPermissions,
  selectRemotePeers,
  selectTracksMap,
  selectUnreadHMSMessagesCount,
  useHMSActions,
  useHMSStore,
  useHMSVanillaStore,
  useScreenShare,
} from "@100mslive/react-sdk";
import {
  Coffee,
  Ellipsis,
  Expand,
  Hand,
  MessageSquare,
  MicOff,
  MonitorUp,
  NotebookText,
  PencilLine,
  PhoneOff,
  PictureInPicture2,
  Settings,
} from "lucide-react";
import {
  ControlButton,
  ControlCaption,
  controlButtonClassName,
} from "@/components/common/ControlButton";
import { ChangeNameDialog } from "@/components/meeting/ChangeNameDialog";
import {
  BlurToggle,
  CameraToggle,
  MicToggle,
} from "@/components/meeting/DeviceControls";
import { MuteAllDialog } from "@/components/meeting/MuteAllDialog";
import { SettingsDialog } from "@/components/settings/SettingsDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMyMetadata } from "@/hooks/useMetadata";
import { SIDE_PANES, useSidePane } from "@/hooks/useSidePane";
import { MediaSession } from "@/lib/pip/mediaSession";
import { PictureInPicture } from "@/lib/pip/PIPManager";

const isScreenShareSupported = () =>
  typeof navigator.mediaDevices?.getDisplayMedia === "function";

const ScreenShareToggle = () => {
  const { amIScreenSharing, toggleScreenShare } = useScreenShare();
  const allowed = useHMSStore(selectIsAllowedToPublish);
  if (!allowed?.screen || !toggleScreenShare || !isScreenShareSupported()) {
    return null;
  }
  return (
    <ControlButton
      data-testid="screen_share_btn"
      label={
        amIScreenSharing ? "Stop sharing your screen" : "Share your screen"
      }
      caption={amIScreenSharing ? "Stop sharing" : "Share"}
      active={amIScreenSharing}
      onClick={() =>
        toggleScreenShare().catch(error =>
          console.error("screen share failed", error)
        )
      }
    >
      <MonitorUp />
    </ControlButton>
  );
};

const PipToggle = ({ onLeave }) => {
  const hmsActions = useHMSActions();
  const store = useHMSVanillaStore();
  const [isOn, setIsOn] = useState(PictureInPicture.isOn());

  useEffect(
    () => () => {
      PictureInPicture.stop().catch(() => {});
    },
    []
  );

  // Keep the PiP window fed with the current remote tracks.
  const remotePeers = useHMSStore(selectRemotePeers);
  const tracksMap = useHMSStore(selectTracksMap);
  useEffect(() => {
    if (isOn) {
      PictureInPicture.updatePeersAndTracks(remotePeers, tracksMap).catch(
        () => {}
      );
    }
  }, [isOn, remotePeers, tracksMap]);

  if (!PictureInPicture.isSupported()) {
    return null;
  }

  const toggle = () => {
    if (isOn) {
      PictureInPicture.stop().catch(() => {});
      return;
    }
    PictureInPicture.start(hmsActions, setIsOn)
      .then(() =>
        PictureInPicture.updatePeersAndTracks(
          store.getState(selectRemotePeers),
          store.getState(selectTracksMap)
        )
      )
      .catch(error =>
        console.error("could not start picture in picture", error)
      );
    MediaSession.setup(hmsActions, store, onLeave);
  };

  return (
    <ControlButton
      data-testid="pip_btn"
      label={
        isOn
          ? "Pop back in (picture in picture)"
          : "Pop out (picture in picture)"
      }
      caption={isOn ? "Pop back in" : "Pop out"}
      active={isOn}
      onClick={toggle}
    >
      <PictureInPicture2 />
    </ControlButton>
  );
};

const MoreMenu = () => {
  const permissions = useHMSStore(selectPermissions);
  const [dialog, setDialog] = useState(null);
  const canFullscreen =
    typeof document !== "undefined" && document.fullscreenEnabled;
  const [isFullscreen, setIsFullscreen] = useState(
    Boolean(document.fullscreenElement)
  );

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const close = () => setDialog(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={controlButtonClassName}
            aria-label="More options"
            title="More options"
            data-testid="more_settings_btn"
          >
            <Ellipsis />
            <ControlCaption>More</ControlCaption>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" side="top" className="w-52">
          <DropdownMenuItem
            data-testid="change_name_btn"
            onSelect={() => setDialog("name")}
          >
            <PencilLine />
            Change Name
          </DropdownMenuItem>
          {canFullscreen ? (
            <DropdownMenuItem
              data-testid="full_screen_btn"
              onSelect={toggleFullscreen}
            >
              <Expand />
              {isFullscreen ? "Exit Fullscreen" : "Go Fullscreen"}
            </DropdownMenuItem>
          ) : null}
          {permissions?.mute ? (
            <DropdownMenuItem
              data-testid="mute_all_btn"
              onSelect={() => setDialog("muteAll")}
            >
              <MicOff />
              Mute All
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            data-testid="device_settings_btn"
            onSelect={() => setDialog("settings")}
          >
            <Settings />
            Settings
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog === "name" ? (
        <ChangeNameDialog open onOpenChange={close} />
      ) : null}
      {dialog === "muteAll" ? (
        <MuteAllDialog open onOpenChange={close} />
      ) : null}
      {dialog === "settings" ? (
        <SettingsDialog open onOpenChange={close} />
      ) : null}
    </>
  );
};

const LeaveButton = ({ onLeave }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="destructive"
        className="h-auto min-w-10 flex-col gap-1 rounded-md px-3 py-2 sm:min-w-14 sm:py-1.5 [&_svg:not([class*='size-'])]:size-5"
        aria-label="Leave the interview"
        data-testid="leave_room_btn"
        onClick={onLeave}
      >
        <PhoneOff />
        <ControlCaption>Leave</ControlCaption>
      </Button>
    </TooltipTrigger>
    <TooltipContent>Leave the interview</TooltipContent>
  </Tooltip>
);

const ChatToggle = () => {
  const { sidePane, toggleSidePane } = useSidePane();
  const unread = useHMSStore(selectUnreadHMSMessagesCount);
  const isOpen = sidePane === SIDE_PANES.chat;
  return (
    <ControlButton
      data-testid="chat_btn"
      label={isOpen ? "Close chat" : "Open chat"}
      caption="Chat"
      active={isOpen}
      onClick={() => toggleSidePane(SIDE_PANES.chat)}
      className="relative"
    >
      <MessageSquare />
      {unread > 0 && !isOpen ? (
        <span
          className="absolute top-1 right-3 size-2.5 rounded-full bg-brand ring-2 ring-card"
          data-testid="chat_unread_btn"
        />
      ) : null}
    </ControlButton>
  );
};

/**
 * Publishes the bar's height as `--control-bar-height` so toasts sit above it
 * (see the Toaster in App.jsx). On a phone the bar wraps to two rows.
 */
function useControlBarHeight(footerRef) {
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) {
      return undefined;
    }
    const root = document.documentElement;
    const observer = new ResizeObserver(() => {
      root.style.setProperty(
        "--control-bar-height",
        `${footer.offsetHeight}px`
      );
    });
    observer.observe(footer);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--control-bar-height");
    };
  }, [footerRef]);
}

/** The call controls along the bottom of the screen. */
export const ControlBar = ({ panelAvailable }) => {
  const navigate = useNavigate();
  const { roomId, role } = useParams();
  const footerRef = useRef(null);
  useControlBarHeight(footerRef);
  const hmsActions = useHMSActions();
  const {
    isHandRaised,
    isBRBOn,
    isInterviewOn,
    toggleHandRaise,
    toggleBRB,
    toggleInterview,
  } = useMyMetadata();

  const goToLeavePage = () => navigate(`/leave/${roomId}/${role}`);
  const leave = async () => {
    try {
      await hmsActions.leave();
    } finally {
      goToLeavePage();
    }
  };

  return (
    <footer
      ref={footerRef}
      className="flex shrink-0 flex-wrap items-center justify-center gap-1 border-t bg-card px-2 py-1.5 sm:justify-between sm:px-4"
      data-testid="footer"
    >
      <div className="flex items-center gap-1">
        <MicToggle />
        <CameraToggle />
        <BlurToggle />
      </div>
      <div className="flex items-center gap-1">
        <ScreenShareToggle />
        {panelAvailable ? (
          <ControlButton
            data-testid="interview_btn"
            label={`${isInterviewOn ? "Close" : "Open"} interview details`}
            caption="Interview"
            active={isInterviewOn}
            onClick={toggleInterview}
          >
            <NotebookText />
          </ControlButton>
        ) : null}
        <ControlButton
          data-testid="raise_hand_btn"
          label={isHandRaised ? "Lower hand" : "Raise hand"}
          caption={isHandRaised ? "Lower hand" : "Raise hand"}
          active={isHandRaised}
          onClick={toggleHandRaise}
        >
          <Hand />
        </ControlButton>
        <ControlButton
          data-testid="brb_btn"
          label={isBRBOn ? "I'm back" : "Away (I'll be right back)"}
          caption={isBRBOn ? "I'm back" : "Away"}
          active={isBRBOn}
          onClick={toggleBRB}
        >
          <Coffee />
        </ControlButton>
        <PipToggle onLeave={goToLeavePage} />
        <MoreMenu />
      </div>
      <div className="flex items-center gap-1">
        <ChatToggle />
        <LeaveButton onLeave={leave} />
      </div>
    </footer>
  );
};
